import React, { useState, useEffect } from 'react';
import { FiX, FiSend, FiCalendar, FiUser, FiMail, FiPhone, FiMessageSquare } from 'react-icons/fi';
import * as api from '../../services/api';

const ScheduleMeetPanel = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Lock body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Reset form when panel opens
  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setError('');
    }
  }, [isOpen]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.createLead({
        name: form.name,
        email: form.email,
        phone: form.phone,
        serviceInterested: form.projectType || 'Schedule Meet',
        message: form.message || 'Meeting request via Schedule Meet panel',
        source: 'schedule_meet',
      });
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', projectType: '', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className={`meet-panel__backdrop ${isOpen ? 'meet-panel__backdrop--active' : ''}`}
        onClick={onClose}
      />

      {/* Slide-in panel */}
      <div className={`meet-panel ${isOpen ? 'meet-panel--open' : ''}`}>
        {/* Header */}
        <div className="meet-panel__header">
          <div className="meet-panel__header-icon">
            <FiCalendar />
          </div>
          <div>
            <h2 className="meet-panel__title">Schedule a Meet</h2>
            <p className="meet-panel__subtitle">
              Let's discuss your ideas and turn them into reality.
            </p>
          </div>
          <button className="meet-panel__close" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        {/* Body */}
        <div className="meet-panel__body">
          {submitted ? (
            <div className="meet-panel__success">
              <div className="meet-panel__success-icon">✓</div>
              <h3>Meeting Request Sent!</h3>
              <p>Our team will reach out to you within 24 hours to confirm your meeting slot.</p>
              <button className="meet-panel__btn" onClick={onClose}>Close</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="meet-panel__form">
              <p className="meet-panel__form-intro">
                Whether it's a website, mobile app, or a full-scale digital platform — our team is ready to guide you from idea to execution. This meet is <strong>free, confidential</strong>, and designed to give you clarity on your next steps.
              </p>

              {error && <div className="meet-panel__error">{error}</div>}

              {/* Project Type */}
              <div className="meet-panel__field">
                <label className="meet-panel__label">Project Type</label>
                <select
                  name="projectType"
                  value={form.projectType}
                  onChange={handleChange}
                  className="meet-panel__select"
                >
                  <option value="">Select project type</option>
                  <option value="Website Development">Website Development</option>
                  <option value="Mobile App">Mobile App</option>
                  <option value="Custom Software">Custom Software</option>
                  <option value="UI/UX Design">UI/UX Design</option>
                  <option value="Digital Marketing">Digital Marketing</option>
                  <option value="SEO Optimization">SEO Optimization</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Full Name */}
              <div className="meet-panel__field">
                <label className="meet-panel__label"><FiUser className="meet-panel__field-icon" /> Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="meet-panel__input"
                  required
                />
              </div>

              {/* Email */}
              <div className="meet-panel__field">
                <label className="meet-panel__label"><FiMail className="meet-panel__field-icon" /> Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="meet-panel__input"
                  required
                />
              </div>

              {/* Phone */}
              <div className="meet-panel__field">
                <label className="meet-panel__label"><FiPhone className="meet-panel__field-icon" /> Mobile Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXX XXXXX"
                  className="meet-panel__input"
                  required
                />
              </div>

              {/* Message */}
              <div className="meet-panel__field">
                <label className="meet-panel__label"><FiMessageSquare className="meet-panel__field-icon" /> Brief about your project</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us briefly about your idea..."
                  className="meet-panel__textarea"
                  rows="3"
                />
              </div>

              <button
                type="submit"
                className="meet-panel__btn meet-panel__btn--submit"
                disabled={submitting}
              >
                <FiSend /> {submitting ? 'Sending...' : 'Request a Meet'}
              </button>

              <p className="meet-panel__disclaimer">
                🔒 Your information is secure. We'll never share your details.
              </p>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ScheduleMeetPanel;
